import { CustomerProfileButton } from "src/common/customerSharing/CustomerSharing";
import { useEffect, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    FileText,
    FolderOpen,
    Globe2,
    History,
    IndianRupee,
    ListPlus,
    Mail,
    MapPin,
    Pencil,
    Phone,
    Route,
    UserRound,
    Zap,
    X,
} from "lucide-react";

import DeleteLeadsDialog from "./components/Leads/DeleteLeadsDialog";
import LeadDetails from "./LeadDetails";
import LeadModal from "./components/Leads/LeadModal";
import { NextStepBar, StageStepper } from "src/features/Stage/shared/LeadFocus";

const LEAD_SECTIONS = [
    { id: "quick-actions", icon: Zap, label: "Actions" },
    { id: "lead-workflow", icon: Route, label: "Workflow" },
    { id: "workflow-timeline", icon: History, label: "History" },
    { id: "lead-info", icon: UserRound, label: "Lead info" },
    { id: "lead-additional-details", icon: ListPlus, label: "Additional details" },
    { id: "lead-details", icon: FileText, label: "Activities" },
    { id: "lead-documents", icon: FolderOpen, label: "Documents" },
];

function initials(name) {
    if (!name) return "L";

    return name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]).join("").toUpperCase();
}

function cleanUrl(url) {
    return url?.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\/$/, "") || "";
}

function websiteUrl(url) {
    if (!url) return "";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function formatRevenue(value) {
    if (!value) return "";
    const numericValue = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(numericValue) ? numericValue.toLocaleString("en-IN") : value;
}

function DetailPill({ icon: Icon, children, href, title }) {
    if (!children) return null;

    const className = "inline-flex min-w-0 items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-slate-600";
    const content = (
        <>
            <Icon className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate">{children}</span>
        </>
    );

    if (href) {
        const opensNewTab = href.startsWith("http");

        return (
            <a
                href={href}
                target={opensNewTab ? "_blank" : undefined}
                rel={opensNewTab ? "noopener noreferrer" : undefined}
                title={title}
                className={`${className} transition hover:bg-blue-50 hover:text-blue-700`}
            >
                {content}
            </a>
        );
    }

    return <div title={title} className={className}>{content}</div>;
}

function SectionButton({ icon: Icon, label, onClick, active = false }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            aria-current={active ? "location" : undefined}
            className={`crm-drawer-tab${active ? " is-active" : ""}`}
        >
            <Icon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{label}</span>
        </button>
    );
}

function LeadHeaderSkeleton() {
    return (
        <div className="animate-pulse">
            <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-2xl bg-slate-200" />
                <div className="space-y-2">
                    <div className="h-5 w-48 rounded bg-slate-200" />
                    <div className="h-4 w-32 rounded bg-slate-100" />
                </div>
            </div>
            <div className="mt-5 flex gap-2">
                <div className="h-9 w-36 rounded-lg bg-slate-100" />
                <div className="h-9 w-36 rounded-lg bg-slate-100" />
            </div>
        </div>
    );
}

export default function LeadDrawer({
    open,
    leadId,
    workspaceId,
    onClose,
    onDeleted,
    onOpenCustomer,
    onPrevLead,
    onNextLead,
    hasPrev,
    hasNext,
}) {
    const [deleteLead, setDeleteLead] = useState(null);
    const [lead, setLead] = useState(null);
    const fieldEnabled = (key) => !lead?.lead_schema || lead.lead_schema.some((field) => field.key === key);
    const [showEditModal, setShowEditModal] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [currentLeadId, setCurrentLeadId] = useState(leadId);
    const [maxValidLeadId, setMaxValidLeadId] = useState(null);
    const [activeSection, setActiveSection] = useState("quick-actions");

    useEffect(() => {
        if (!open) return;

        setCurrentLeadId(leadId);
        setMaxValidLeadId(null);
        setLead(null);
    }, [open, leadId]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };

        if (open) {
            document.body.style.overflow = "hidden";
            window.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.body.style.overflow = "unset";
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, onClose]);

    useEffect(() => {
        if (!open) return undefined;

        const drawer = document.getElementById("lead-drawer");
        if (!drawer) return undefined;

        let animationFrame;

        const updateActiveSection = () => {
            window.cancelAnimationFrame(animationFrame);
            animationFrame = window.requestAnimationFrame(() => {
                const stickyHeight = drawer.querySelector("[data-drawer-sticky]")?.offsetHeight || 56;
                const threshold = drawer.getBoundingClientRect().top + stickyHeight + 32;
                const visibleSection = LEAD_SECTIONS
                    .map(({ id }) => drawer.querySelector(`#${id}`))
                    .filter(Boolean)
                    .filter((section) => section.getBoundingClientRect().top <= threshold)
                    .at(-1);

                setActiveSection(visibleSection?.id || "quick-actions");
            });
        };

        updateActiveSection();
        drawer.addEventListener("scroll", updateActiveSection, { passive: true });

        return () => {
            drawer.removeEventListener("scroll", updateActiveSection);
            window.cancelAnimationFrame(animationFrame);
        };
    }, [open, currentLeadId, lead]);

    if (!open) return null;

    const handleLeadFetched = (fetchedLead) => {
        if (!fetchedLead) {
            if (currentLeadId > 1) {
                setMaxValidLeadId(currentLeadId - 1);
                setCurrentLeadId((previous) => previous - 1);
            }
            return;
        }

        setLead(fetchedLead);
    };

    const isPrevAvailable = (() => {
        if (hasPrev !== undefined) return Boolean(hasPrev);
        if (lead?.has_prev !== undefined) return Boolean(lead.has_prev);
        if (lead?.hasPrev !== undefined) return Boolean(lead.hasPrev);
        if (lead?.prev_id !== undefined) return lead.prev_id !== null;
        if (onPrevLead) return true;
        return Boolean(currentLeadId && currentLeadId > 1);
    })();

    const isNextAvailable = (() => {
        if (hasNext !== undefined) return Boolean(hasNext);
        if (lead?.has_next !== undefined) return Boolean(lead.has_next);
        if (lead?.hasNext !== undefined) return Boolean(lead.hasNext);
        if (lead?.next_id !== undefined) return lead.next_id !== null;
        if (onNextLead) return true;
        if (maxValidLeadId !== null && currentLeadId >= maxValidLeadId) return false;
        return Boolean(lead?.id);
    })();

    const scrollToSection = (id) => {
        const drawer = document.getElementById("lead-drawer");
        const section = drawer?.querySelector(`#${id}`);

        if (!drawer || !section) return;

        const headerHeight = Array.from(drawer.querySelectorAll("[data-drawer-sticky]"))
            .reduce((height, element) => height + element.offsetHeight, 0) || 56;
        const sectionTop = section.getBoundingClientRect().top
            - drawer.getBoundingClientRect().top
            + drawer.scrollTop;

        drawer.scrollTo({ top: sectionTop - headerHeight - 16, behavior: "smooth" });
    };

    const scrollToTop = () => {
        document.getElementById("lead-drawer")?.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handlePrevLead = () => {
        if (!isPrevAvailable) return;
        if (onPrevLead) {
            onPrevLead();
            scrollToTop();
            return;
        }

        const previousId = lead?.prev_id || (currentLeadId ? currentLeadId - 1 : null);
        if (!previousId || previousId < 1) return;

        setLead(null);
        setCurrentLeadId(previousId);
        scrollToTop();
    };

    const handleNextLead = () => {
        if (!isNextAvailable) return;
        if (onNextLead) {
            onNextLead();
            scrollToTop();
            return;
        }

        const nextId = lead?.next_id || (currentLeadId ? currentLeadId + 1 : null);
        if (!nextId) return;

        setLead(null);
        setCurrentLeadId(nextId);
        scrollToTop();
    };

    const stageName = lead?.stage?.name || lead?.current_state?.current_node?.name;

    return (
        <>
            {deleteLead && <DeleteLeadsDialog leadIds={[deleteLead.id]} leadName={deleteLead.name} workspaceId={workspaceId} onClose={() => setDeleteLead(null)} onDeleted={() => { setDeleteLead(null); onClose(); onDeleted?.(); }} />}
            <button
                type="button"
                aria-label="Close lead details"
                className="fixed inset-0 z-40 cursor-default bg-[rgba(16,47,69,0.32)] backdrop-blur-[3px]"
                onClick={onClose}
            />

            <aside
                id="lead-drawer"
                role="dialog"
                aria-modal="true"
                aria-label={lead?.name ? `${lead.name} lead details` : "Lead details"}
                className="crm-drawer fixed inset-y-0 right-0 z-50 w-full overflow-y-auto shadow-[-24px_0_64px_-24px_rgba(15,23,42,0.35)] sm:max-w-[860px]"
            >
                <div data-drawer-sticky className="crm-drawer-bar sticky top-0 z-50 flex h-14 items-center gap-2 px-3 sm:px-5">
                    <span className="crm-drawer-id ws-num">#{lead?.id || currentLeadId}</span>
                    <div className="h-5 w-px shrink-0 bg-slate-200" />

                    <nav className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="Lead sections">
                        {LEAD_SECTIONS.map(({ id, icon, label }) => (
                            <SectionButton key={id} active={activeSection === id} icon={icon} label={label} onClick={() => scrollToSection(id)} />
                        ))}
                    </nav>

                    <div className="flex shrink-0 items-center gap-0.5 border-l border-slate-200 pl-2">
                        <button type="button" onClick={handlePrevLead} disabled={!isPrevAvailable} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-30" title="Previous lead" aria-label="Previous lead">
                            <ChevronLeft className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={handleNextLead} disabled={!isNextAvailable} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-30" title="Next lead" aria-label="Next lead">
                            <ChevronRight className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={onClose} className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition hover:bg-rose-50 hover:text-rose-600" aria-label="Close lead">
                            <X className="h-[18px] w-[18px]" />
                        </button>
                    </div>
                </div>

                <header className="crm-drawer-head">
                    <div className="px-4 py-5 sm:px-7 sm:py-6">
                        {!lead ? (
                            <LeadHeaderSkeleton />
                        ) : (
                            <>
                                <div className="flex flex-wrap items-start gap-3.5">
                                    <div className="crm-drawer-avatar">
                                        {initials(lead.name)}
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h1 className="crm-drawer-name">{lead.name || "Unnamed lead"}</h1>
                                            {stageName && <span className={`crm-stage-pill${lead?.stage?.is_closed ? (Number(lead.stage.probability) >= 100 ? " is-won" : " is-lost") : ""}`}><i aria-hidden="true" />{stageName}</span>}
                                            {fieldEnabled("source") && lead.source && <span className="ws-chip">{lead.source}</span>}
                                        </div>

                                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                                            {fieldEnabled("designation") && lead.designation && <span>{lead.designation}</span>}
                                            {fieldEnabled("designation") && lead.designation && fieldEnabled("company") && lead.company && <span className="text-slate-300">•</span>}
                                            {fieldEnabled("company") && lead.company && <span className="font-medium text-slate-700">{lead.company}</span>}
                                        </div>
                                    </div>

                                    {lead.permissions?.delete && <button type="button" onClick={() => setDeleteLead(lead)} className="ws-btn ws-btn--quiet ws-btn--sm crm-btn-danger-text">Delete</button>}
                                    <CustomerProfileButton onOpen={onOpenCustomer ? () => onOpenCustomer(lead.id) : undefined} recordId={lead.id} name={lead.name} record={lead} workspaceId={workspaceId} onEdit={() => setShowEditModal(true)} />
                                    <button type="button" onClick={() => setShowEditModal(true)} className="ws-btn ws-btn--primary ws-btn--sm">
                                        <Pencil className="h-3.5 w-3.5" />
                                        <span className="hidden sm:inline">Edit</span>
                                    </button>
                                </div>

                                <div className="crm-drawer-details">
                                    {fieldEnabled("email") && <DetailPill icon={Mail} href={lead.email ? `mailto:${lead.email}` : ""} title={lead.email}>{lead.email}</DetailPill>}
                                    {fieldEnabled("phone") && <DetailPill icon={Phone} href={lead.phone ? `tel:${lead.phone}` : ""} title={lead.phone}>{lead.phone}</DetailPill>}
                                    {fieldEnabled("company_location") && <DetailPill icon={MapPin} title={lead.company_location}>{lead.company_location}</DetailPill>}
                                    {fieldEnabled("website") && <DetailPill icon={Globe2} href={websiteUrl(lead.website)} title={lead.website}>{cleanUrl(lead.website)}</DetailPill>}
                                    {fieldEnabled("revenue") && <DetailPill icon={IndianRupee} title={lead.revenue ? `Revenue: ${formatRevenue(lead.revenue)}` : ""}>{formatRevenue(lead.revenue)}</DetailPill>}
                                </div>

                                <StageStepper key={`stages-${lead.id}-${lead.lead_stage_id}`} lead={lead} onMoved={() => setRefreshKey((previous) => previous + 1)} />
                                <div className="crm-drawer-next"><NextStepBar lead={lead} onChanged={() => setRefreshKey((previous) => previous + 1)} /></div>
                            </>
                        )}
                    </div>
                </header>

                <main className="px-4 py-4 sm:px-7 sm:py-5">
                    {currentLeadId != null && (
                        <LeadDetails key={currentLeadId} leadId={currentLeadId} workspaceId={workspaceId} onLeadFetched={handleLeadFetched} refreshKey={refreshKey} onOpenLead={(id) => { setLead(null); setCurrentLeadId(id); }} />
                    )}
                </main>
            </aside>

            {showEditModal && (
                <LeadModal
                    leadId={currentLeadId}
                    workspaceId={workspaceId}
                    pipelineId={lead?.stage?.pipeline_id}
                    onClose={() => setShowEditModal(false)}
                    onSuccess={() => {
                        setRefreshKey((previous) => previous + 1);
                        setShowEditModal(false);
                    }}
                />
            )}
        </>
    );
}
